import React, { useState, useEffect, useMemo } from "react"
import PropTypes from "prop-types"
import { compose, withApollo } from "react-apollo"
import { useQuery, useMutation } from "@apollo/react-hooks"

import { TOPIC_QUERY, TOPIC_EDIT_MUTATION } from "./query.js"
import { ALL_COUNTRIES, LANGUAGE_OPTIONS } from "../../consts"
import { getRole, getPromotionAsTarget } from "../../utils"
import TabPage from "../../components/TabPage"
import TopicEditHeader from "../../components/TopicEditHeader"
import ToastStore from "../../stores/ToastStore"
import TermsAndQueries from "./tabs/TermsAndQueries"
import { ContextOrNichesWrapper } from "./tabs/ContextOrNichesWrapper"
import { PromotedCategoryHeader } from "./tabs/PromotedCategory/PromotedCategoryHeader"

import "./index.scss"

const TopicEditPage = ({ client, history, location, match }) => {
  const { params, url } = match
  const topicId = Number(params.topicId)

  // local state
  const [inputState, setInputState] = useState({ name: "" })
  const [initialName, setInitialName] = useState("")

  // queries and mutations
  const {
    data: topicData,
    loading: topicLoading,
    error: topicError,
    refetch: topicRefetch
  } = useQuery(TOPIC_QUERY, {
    notifyOnNetworkStatusChange: true,
    fetchPolicy: "network-only",
    client,
    variables: {
      topicIds: [topicId]
    },
    onCompleted: ({ topics }) => {
      const topic = topics?.[0]

      if (!topic) return

      setInitialName(topic.title)
      setInputState({
        name: topic.title
      })
    }
  })
  const [topicEdit] = useMutation(TOPIC_EDIT_MUTATION, {
    client,
    onError(e) {
      console.error("TopicEditPage @ topicEdit @ onError >>>>>", e)
      ToastStore.clearToasts()
      ToastStore.addToast({
        title: "Oops! An error ocurred. Please try again.",
        id: "error"
      })
    },
    onCompleted({ editTopicTitle: { success, message } }) {
      if (success) {
        history.push({
          pathname: "/topic/success",
          state: { backgroundPage: location }
        })
      } else {
        console.error("TopicEditPage @ topicEdit @ onCompleted >>>>>", message)
        ToastStore.clearToasts()
        ToastStore.addToast({
          title: `Oops! Name was not updated. Please try again.`,
          id: "edit-error"
        })
      }
    }
  })

  // A promoted category (N1) reuses this page in read-only mode. It is detected
  // from the topic's `promotionsV2`: the topic is the `target` of its own link.
  const topic = topicData?.topics?.[0]
  const promotion = getPromotionAsTarget(topic)
  const isPromoted = Boolean(promotion)
  const promotedFrom = useMemo(
    () =>
      promotion
        ? {
            nicheName: promotion.source?.name,
            parentTopicName: promotion.parent?.name,
            parentTopicId: promotion.parent?.territoryId?.split(".")[1] || null
          }
        : null,
    [promotion]
  )

  // effects
  useEffect(() => {
    // on "successful change modal" closing, refetch topic
    if (location?.state?.refetch) {
      topicRefetch()
    }
  }, [location])

  // functions
  const getTopicLanguage = languageInitials => {
    if (!languageInitials) return "Undefined"

    const languageOption = LANGUAGE_OPTIONS.find(
      languageOption => languageOption.value === languageInitials
    )

    return languageOption?.label || "Global"
  }
  const handleInputChange = (value, name) => {
    setInputState(inputState => ({
      ...inputState,
      [name]: value
    }))
  }
  const handleSave = () => {
    const variables = {
      id: topicId,
      title: inputState.name
    }

    topicEdit({
      variables
    })
  }
  const onReset = () => {
    setInputState({
      name: initialName
    })
  }

  // rendering
  const renderHeader = () => {
    if (!topic) return null

    if (isPromoted) {
      return (
        <PromotedCategoryHeader
          name={topic.title}
          categoryTitle={topic.category?.title}
          languageLabel={getTopicLanguage(topic.language)}
          countryLabel={
            ALL_COUNTRIES.find(
              countryOption => countryOption.ALPHA_2 === topic.country
            )?.NAME
          }
          promotedFrom={promotedFrom}
        />
      )
    }

    const role = getRole()

    const {
      language,
      category: { title: categoryTitle },
      country
    } = topic
    const topicLanguage = getTopicLanguage(language)
    const isResetDisabled = inputState.name === initialName
    const isSaveDisabled = isResetDisabled || !inputState.name.length
    const countryLabel = ALL_COUNTRIES.find(
      countryOption => countryOption.ALPHA_2 === country
    )?.NAME

    return (
      <TopicEditHeader
        {...{
          handleInputChange,
          handleSave,
          onReset,
          initialName,
          isResetDisabled,
          isSaveDisabled,
          categoryTitle,
          topicLanguage,
          inputState,
          role,
          countryLabel
        }}
      />
    )
  }

  const tabs = useMemo(() => {
    if (isPromoted) {
      return [
        {
          path: "/queries",
          // The promoted category edits its own query like any normal topic,
          // and surfaces the "Inherited from niche" banner for its origin.
          component: () => (
            <TermsAndQueries
              topic={topic}
              isPromoted={isPromoted}
              promotedFrom={promotedFrom}
            />
          ),
          label: "Terms and queries"
        },
        {
          path: "/context/:contextId?",
          linkPath: "/context",
          component: props => (
            <ContextOrNichesWrapper
              topicId={String(topicId)}
              match={props.match}
              history={history}
              location={props.location}
              isReadOnly
              excludeContextId={promotion?.sourceContextId}
            />
          ),
          label: "Context"
        }
      ]
    }

    const shouldShowContextTab = !!topic?.country && !topic?.language
    const topicCountryLabel = ALL_COUNTRIES.find(
      countryOption => countryOption.ALPHA_2 === topic?.country
    )?.NAME

    if (!shouldShowContextTab) {
      return [
        {
          path: "/queries",
          component: () => <TermsAndQueries topic={topic} />,
          label: "Terms and queries"
        }
      ]
    }

    return [
      {
        path: "/queries",
        component: () => <TermsAndQueries topic={topic} />,
        label: "Terms and queries"
      },
      {
        path: "/context/:contextId?",
        linkPath: "/context",
        component: props => (
          <ContextOrNichesWrapper
            topicId={String(topic?.topicId)}
            match={props.match}
            history={history}
            location={props.location}
            topicName={topic?.title}
            topicCategory={topic?.category?.title}
            topicCountry={topicCountryLabel}
          />
        ),
        label: "Context"
      }
    ]
  }, [history, isPromoted, promotedFrom, promotion, topic, topicId])

  // A non-promoted topic that the query did not return (e.g. a non-existent id)
  // shows the error state instead of crashing the header.
  const topicNotFound = !isPromoted && !topicLoading && !topicData?.topics?.[0]

  return (
    <TabPage
      className="TopicEditPage"
      renderHeader={renderHeader}
      loading={topicLoading}
      error={Boolean(topicError) || topicNotFound}
      url={url}
      tabs={tabs}
    />
  )
}

TopicEditPage.propTypes = {
  client: PropTypes.object.isRequired,
  history: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired
}

export default compose(withApollo)(TopicEditPage)
